import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const FIELDS = [
  ["title", "Title", "col-span-2"], ["artist", "Artist", ""], ["album_artist", "Album artist", ""],
  ["album", "Album", "col-span-2"], ["track_no", "Track #", ""], ["year", "Year", ""], ["genre", "Genre", "col-span-2"],
];
const inputCls = "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none transition-colors focus:border-zinc-900";

export const EditTrackDialog = ({ track, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (track) setF({ title: track.title || "", artist: track.artist || "", album: track.album || "", album_artist: track.album_artist || "",
      track_no: track.track_no ?? "", year: track.year ?? "", genre: track.genre || "" });
  }, [track]);
  if (!track) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      for (const [k] of FIELDS) {
        const v = String(f[k] ?? "").trim();
        if (k === "track_no" || k === "year") { if (v !== "" && String(track[k] ?? "") !== v) body[k] = Number(v); }
        else if (v !== (track[k] || "") && (v || k === "genre" || k === "album_artist")) body[k] = v;
      }
      if (!Object.keys(body).length) { onClose(); return; }
      const { data } = await api.patch(`/storage/tracks/${track.id}`, body);
      toast.success(data.tags_written ? "Details saved into the file" : "Details saved");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-track-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-2xl"><Pencil size={18} />Edit song details</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label, cls]) => (
            <label key={k} className={`block ${cls}`}>
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</span>
              <input value={f[k] ?? ""} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`edit-track-${k}`}
                className={inputCls} type={k === "track_no" || k === "year" ? "number" : "text"} disabled={busy} />
            </label>
          ))}
          <p className="col-span-2 text-[11px] text-zinc-500">Changes are written into the audio file in Resonance storage. Changing the album or album artist moves the song to that album.</p>
          <DialogFooter className="col-span-2 mt-2">
            <button type="button" onClick={onClose} disabled={busy} data-testid="edit-track-cancel" className="h-10 rounded-full px-5 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
            <button type="submit" disabled={busy} data-testid="edit-track-save" className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm text-white hover:bg-zinc-700 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />}Save changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
